import { useMemo } from 'react';

import { Dimensions, Metrics } from 'components/insights/constants';
import { getInboundSlideoutEntityFilter, useInboundReferralsInsightsStore } from 'stores/inboundReferralsInsightsStore';

import { getInboundEntitySlideoutParams, getInboundMetricSlideoutParams } from '../constants';
import useInboundReferralAnalytics from '../query/useInboundReferralAnalytics';

// Runs the active View-By query for the open slideout, scoped to the clicked entity (via
// extraFilters). One rollups query feeds both the breakdown table (grouping-0 rows) and the header
// tiles (the fully-rolled-up `overall` row) — the inbound source needs no per-tile fan-out.
export default function useInboundSlideoutAnalytics() {
  const slideoutContext = useInboundReferralsInsightsStore((state) => state.slideoutContext);
  const slideoutDimension = useInboundReferralsInsightsStore((state) => state.slideoutDimension);

  const extraFilters = useMemo(
    () => (slideoutContext ? getInboundSlideoutEntityFilter(slideoutContext) : null),
    [slideoutContext]
  );

  // Fall back to a valid dimension so the hook always has params; the query is gated by `enabled`.
  // Metric drills request their own metric set; entity drills request the determination set. No
  // `:with_id` — slideout rows aren't drillable (that's a landing-page affordance), so the group id
  // column isn't needed here.
  const params = useMemo(() => {
    const dimension = slideoutDimension ?? Dimensions.FACILITY;
    return slideoutContext?.kind === 'metric'
      ? getInboundMetricSlideoutParams(slideoutContext.metric, dimension)
      : getInboundEntitySlideoutParams(dimension);
  }, [slideoutContext, slideoutDimension]);

  const enabled = !!slideoutContext && !!slideoutDimension;

  const { data, rollups, loading } = useInboundReferralAnalytics(params, {
    enabled,
    extraFilters,
    queryKey: `inbound-slideout-view-by:${slideoutDimension ?? 'none'}`,
  });

  // Distinct receiving-SNF count for the subtitle: COUNT(DISTINCT facility) rides along on the same
  // query as a metric, read off the fully-rolled-up row — no separate request needed.
  const snfCount = rollups.overall.metrics.getCurrentDataPoint(Metrics.FACILITY_COUNT, 0) ?? 0;

  return {
    data,
    rollups,
    loading: enabled && loading,
    extraFilters,
    snfCount,
  };
}
